import { useRoute, useRouter } from "vue-router";

import isUnauth from "@/utils/isUnauth";
import telemetry from "@/utils/telemetry";

interface TelemetryOptions {
  /** Custom handler called on telemetry success. If not provided, navigates to resultLink */
  onSuccess?: (resultLink: string) => void | Promise<void>;
  /** Custom handler called on telemetry failure. Default is silent (do nothing) */
  onError?: (error: any) => void;
  /** Transform the telemetry data before sending */
  transformData?: (baseData: TelemetryData) => TelemetryData;
  /** Whether to skip navigation on success. Useful when onSuccess handles it */
  skipNavigation?: boolean;
}

interface TelemetryData {
  action: string;
  url: string;
  title: string;
  id: string;
  page: string;
  type: string;
  relevance: number;
  sortOrder: number;
  [key: string]: any;
}

interface CardResult {
  title?: string;
  name?: string;
  id?: string;
  guid?: string;
  type?: string;
  lab_type?: string;
  relevance?: number;
  [key: string]: any;
}

interface CardProps {
  result: CardResult;
  index?: number;
  [key: string]: any;
}

/**
 * Composable for handling telemetry in card components
 * Provides consistent telemetry behavior with customization options for special cases
 */
export function useCardTelemetry(options: TelemetryOptions = {}) {
  const router = useRouter();
  const route = useRoute();

  const sendTelemetry = async (
    props: CardProps,
    resultLink: string
  ): Promise<void> => {
    const baseData: TelemetryData = {
      action: "launch-contentType",
      url: resultLink,
      title: props.result.title ?? props.result.name ?? "",
      id: props.result.guid ?? props.result.id ?? "",
      page: route.fullPath,
      type: props.result.type ?? props.result.lab_type ?? "",
      relevance: props.result.relevance ?? 0,
      sortOrder: props.index ?? 0,
    };

    // Apply custom transformation if provided
    const telemetryData = options.transformData
      ? options.transformData(baseData)
      : baseData;

    try {
      await telemetry.external(telemetryData, isUnauth());

      // Handle success
      if (options.onSuccess) {
        await options.onSuccess(resultLink);
      } else if (!options.skipNavigation) {
        router.push(resultLink);
      }
    } catch (error) {
      // Handle error
      if (options.onError) {
        options.onError(error);
      }
      // Default is silent failure (do nothing)
    }
  };

  return {
    sendTelemetry,
  };
}
